import type { CaipNetwork } from "@reown/appkit";
import {
  arbitrum as arbitrumBase,
  base as baseBase,
  hoodi as hoodiBase,
  mainnet as mainnetBase,
  optimism as optimismBase,
} from "viem/chains";

// TODO: Change to Blockdaemon RPC once auth is setup (https://svc.blockdaemon.com/ethereum/hoodi/native)

/**
 * Global asset configuration for the widget
 * This defines which assets and networks are supported across the application
 */
const hoodi: CaipNetwork = {
  ...hoodiBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:560048",
  rpcUrls: {
    default: {
      // TODO: Change to Blockdaemon RPC once auth is setup (https://svc.blockdaemon.com/ethereum/hoodi/native)
      http: ["https://ethereum-hoodi-rpc.publicnode.com/"],
    },
  },
};

const mainnet: CaipNetwork = {
  ...mainnetBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:1",
  rpcUrls: {
    default: {
      http: ["https://eth.merkle.io/"],
    },
  },
};

const optimism: CaipNetwork = {
  ...optimismBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:10",
  rpcUrls: {
    default: {
      http: ["https://mainnet.optimism.io"],
    },
  },
};

const base: CaipNetwork = {
  ...baseBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:8453",
  rpcUrls: {
    default: {
      http: ["https://base.llamarpc.com"],
    },
  },
};

const arbitrum: CaipNetwork = {
  ...arbitrumBase,
  chainNamespace: "eip155",
  caipNetworkId: "eip155:42161",
  rpcUrls: {
    default: {
      http: ["https://arbitrum.drpc.org"],
    },
  },
};

interface AssetConfig {
  symbol: string;
  name: string;
  networks: Array<CaipNetwork>; // Network configuration objects
  enabled: boolean;
}

/**
 * Currently supported assets - restricted to ETH only
 */
const SUPPORTED_ASSETS: AssetConfig[] = [
  {
    symbol: "eth",
    name: "Ethereum",
    networks: [hoodi, arbitrum, base, mainnet, optimism],
    enabled: true,
  },
] as const;

/**
 * Get all supported network configurations
 */
export function getSupportedNetworks() {
  return SUPPORTED_ASSETS.filter((asset) => asset.enabled).flatMap((asset) => asset.networks);
}

/**
 * Get all supported asset symbols in lowercase
 */
export function getSupportedAssetSymbols(): string[] {
  return SUPPORTED_ASSETS.filter((asset) => asset.enabled).map((asset) => asset.symbol);
}

/**
 * Check if an asset symbol is supported
 */
export function isAssetSupported(symbol: string): boolean {
  const normalizedSymbol = symbol.toLowerCase();
  return getSupportedAssetSymbols().includes(normalizedSymbol);
}
